import React, { useContext, useEffect, useState } from 'react';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Box, Stack } from '@mui/system';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DataLoadingError from '@components/common/DataLoadingError';
import { useConfirm } from '@components/common/hooks/useConfirm';
import useSessionDetailsQuery from '@components/common/hooks/api/useSessionDetailsQuery';
import useSessionParticipantsQuery from '@components/common/hooks/api/useSessionParticipantsQuery';
import {
  getDateHeader,
  getSessionName,
  participantAttendedSession, sortByParticipantName
} from '@components/Pages/Events/Sessions/sessionFunctions';
import { API_URLS, axiosRequest } from '@lib/axios';
import { PageContext } from '@lib/contexts/pageContext';
import { scrollToAlert } from '@lib/utils';
import { AbilityContext, Subjects } from '@lib/permissions';
import ParticipantRow from './ParticipantRow';
import CheckIcon from '@mui/icons-material/Check';

const FinalizeParticipants = () => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    setBreadcrumbValue,
    setPageErrorMessage,
  } = useContext(PageContext);

  const { eventID, sessionID } = useParams();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const confirm = useConfirm();

  const queryClient = useQueryClient();

  const { data: session, ...sessionQuery } = useSessionDetailsQuery(sessionID);
  const { data: participants, ...participantsQuery } = useSessionParticipantsQuery(eventID);
  if (participants) {
    participants.sort(sortByParticipantName);
  }

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', session.event);
      setBreadcrumbValue(':session', session);
    }
  }, [session, setBreadcrumbValue]);

  const finalizeMutation = useMutation({
    mutationFn: async () => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${session.id}`,
      data: {
        is_finalized: true,
      }
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['participants'] }),
      ]);
      navigate(`/events/${eventID}/${sessionID}/complete`);
    },
    onError: async (err) => {
      setPageErrorMessage(['Error finalizing attendance', err]);
      console.log('[FinalizeParticipants][save] error:', err);
    },
    onSettled: () => {
      setIsSaving(false);
      scrollToAlert();
    },
  });

  const handleFinalize = async () => {
    const confirmed = await confirm({
      title: 'Finalize Participants',
      content: (
        <Box sx={{ width: '100%' }} key="content">
          <p>To continue, select the Finalize Participants button below. You can update session participants later on if needed.</p>
        </Box>
      ),
      confirmationText: 'Finalize Participants',
      cancellationText: 'Go Back',
    })
      .then(() => true)
      .catch(() => false);

    if (confirmed) {
      setIsSaving(true);
      finalizeMutation.mutate();
    }
  };

  const isLoading = sessionQuery.isLoading || participantsQuery.isLoading;

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (sessionQuery.isError && sessionQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={sessionQuery.error.errors} />;
  }

  if (participantsQuery.isError && participantsQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load participants!'} errors={participantsQuery.error.errors} />;
  }

  if (!session) {
    return (
      <>
        You do not have access, or the session was not found.
      </>
    );
  }

  if (ability.cannot('edit', subject(Subjects.SESSION, session))) {
    return <>You do not have permission to edit this session.</>;
  }

  const sessionName = getSessionName({
    session,
    dateFallback: true,
    useNill: false,
  });

  const eventParticipantCount = participants?.length;
  const sessionAttendeeCount = participants?.filter(p => participantAttendedSession(p, session)).length;

  return (
    <div id="pagewrapper" style={{ padding: 0 }}>
      <div id="content-wrapper">

        <Paper elevation={3} sx={{ mb: 5 }}>
          <Box padding={3}>
            <Typography variant="h2" color="ilstorm.main" sx={{ mb: 0, fontWeight: 700 }} data-cy="session-name">
              {sessionName}
            </Typography>
            <Typography variant="body1" sx={{
              ...theme.typography.h3,
              color: theme.palette.blue.main,
              mb: 2,
            }} data-cy="session-dates">
              {getDateHeader(session)}
            </Typography>

            <Stack direction="row" justifyContent="space-between" sx={{ marginTop: '20px' }}>
              <Button
                variant="contained"
                startIcon={<KeyboardArrowLeftIcon />}
                component={RouterLink}
                to={`/events/${session.event.id}/${session.id}`}
              >
                Back to Session
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon/>}
                disabled={isSaving || !sessionAttendeeCount}
                onClick={handleFinalize}
                data-cy="finalize-button"
              >
                Finalize Participants
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ marginBottom: '16px' }} data-cy="instructions">
          <Typography variant="body1" component="div">Before you can complete this session, you must first finalize
            the participant list.</Typography>
          <ul style={{ margin: 0 }}>
            <li style={{ fontSize: '16px' }}>If a participant attended the session, ensure that the &quot;Session
              Attendance&quot; checkbox is selected.
            </li>
            <li style={{ fontSize: '16px' }}>To add a new participant to the list, click &quot;Add Participant&quot;.
            </li>
            <li style={{ fontSize: '16px' }}>Once the list is finalized, click &quot;Finalize Participants&quot; to
              proceed to the next step.
            </li>
          </ul>

          {!sessionAttendeeCount && (
            <Typography variant="body1" component="div" data-cy="instructions-no-attendees">
              If no one showed up for this session, go back to the Session Details page, click &quot;Cancel
              Session&quot; and select &quot;No participants showed up&quot;</Typography>
          )}
        </Alert>

        <Button
          variant="contained"
          color="primary"
          sx={{ marginBottom: '24px' }}
          onClick={() => navigate(`/events/${eventID}/${sessionID}/participants/add`)}
        >Add Participant</Button>

        {eventParticipantCount > 0 && (
          <>
            <Table sx={{ marginBottom: '24px' }} data-cy="participants-table">
              <TableHead>
                <TableHeadRow>
                  <TableCell component="th">Name</TableCell>
                  <TableCell component="th" style={{ textAlign: 'center' }}>Demographics Collected?</TableCell>
                  <TableCell component="th" style={{ textAlign: 'center' }}>Session Attendance</TableCell>
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {participants.map(p => (
                  <ParticipantRow key={p.id} participant={p} session={session} />
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {!eventParticipantCount && (
          <Typography variant="body1" sx={{ fontSize: '16px' }} data-cy="participants-empty">
            There are no participants.
          </Typography>
        )}

      </div>
    </div>
  );
};

const TableHeadRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    backgroundColor: '#f4f4f4',
    borderColor: '#0000001f',
  },
});

export default FinalizeParticipants;
